/**
 * @name 文件模块
 */
import type { AxiosResponse } from 'axios'
import axiosOrig from 'axios' // 引入原生 axios 用于 OSS 直传，避免被拦截器拦截
import qs from 'qs'
import { baseURL } from '@/api/base' // 导入接口域名列表
import axios from '@/utils/http' // 导入http中创建的axios实例
import { ContentTypeEnum } from '@shared/enums/httpEnum'
import { getToken } from '@/utils/token'
import { TokenKeyOfHeader } from '@/settings'

export type CusFile = {
	fileKey: string
	fileSize: number
	fileStorePah: string
	filename: string
	ossFileUrl: string
}

export interface OssSignature {
	signature: string
	security_token: string
	host: string
	x_oss_credential: string
	dir: string
	version: string
	x_oss_date: string
	policy: string
}

export interface UploadToOssParams {
	fileKey: string
	fileSize: number
	fileStorePath: string
	filename: string
	ossFileUrl: string
}

// * 下载
export const download: (fileKey: string) => Promise<AxiosResponse> = fileKey =>
	axios.get(`${baseURL}/api/sysAttachFile/download`, { params: { fileKey }, responseType: 'blob' })

// * /api/sysAttachFile/downloadOss 下载文件OSS
export const downloadOss = (ossUrl: string): Promise<AxiosResponse> =>
	axios.get(`${baseURL}/api/sysAttachFile/downloadOss`, { params: { ossUrl }, responseType: 'blob', hideLoading: true })

// * 获得图片流
export const getImageStream: (fileKey: string) => Promise<AxiosResponse> = fileKey => axios.get(`${baseURL}/api/sysAttachFile/image`, { params: { fileKey } })
export const getImageUrl: (fileKey: string) => string = fileKey =>
	fileKey ? `${baseURL}/api/sysAttachFile/image?${qs.stringify({ fileKey, [TokenKeyOfHeader]: getToken() })}` : ''

// * 获得图片base64
export const getImageBase64: (fileKey: string) => Promise<{ filename: CusFile['filename']; base64String: string }> = fileKey =>
	axios.get(`${baseURL}/api/sysAttachFile/image/base64`, { params: { fileKey } })

// * 获取OSS授权签名
export const getOssSignature = () =>
	axios.get(`${baseURL}/api/sysAttachFile/get_signature_for_oss_upload`) as Promise<OssSignature>

// * 保存OSS上传记录
export const saveOssRecord = (params: UploadToOssParams) =>
	axios.post(`${baseURL}/api/sysAttachFile/uploadToOss`, params) as Promise<CusFile>

// * 上传 (OSS直传改造版 - V4签名表单上传)
export const upload: (params: FormData) => Promise<CusFile> = async params => {
	const file = params.get('file') as File
	if (!file) {
		return Promise.reject(new Error('未找到文件对象'))
	}

	try {
		// 1. 获取签名
		const sig = await getOssSignature()

		// 2. 构造唯一文件名
		const randomStr = Math.random().toString(36).substring(2, 10)
		const timestamp = new Date().getTime()
		// 移除文件名中的特殊字符，防止 OSS 问题
		const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '')
		const key = `${sig.dir}${timestamp}_${randomStr}_${cleanName}`

		// 3. 构造 FormData
		const formData = new FormData()
		// 注意：字段名必须与 Policy 中定义的一致，顺序建议如下
		formData.append('key', key)
		formData.append('x-oss-signature-version', sig.version)
		formData.append('x-oss-credential', sig.x_oss_credential)
		formData.append('x-oss-date', sig.x_oss_date)
		formData.append('x-oss-security-token', sig.security_token)
		formData.append('policy', sig.policy)
		formData.append('x-oss-signature', sig.signature)
		formData.append('success_action_status', '200') // 让服务端返回 200 OK
		formData.append('file', file) // file 必须放在最后

		// 4. 直传 OSS (使用原生 axios 避免拦截器干扰)
		// 注意：host 通常就是上传的 URL
		await axiosOrig.post(sig.host, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		})

		// 5. 组装 ossFileUrl
		// 如果 host 包含 bucket (如 https://bucket.oss-cn-hz.aliyuncs.com)，直接拼接
		// 如果 host 是 endpoint (如 https://oss-cn-hz.aliyuncs.com)，需要确认 bucket
		// 这里假设 host 是完整的上传域名
		const ossFileUrl = `${sig.host}/${key}`

		// 6. 调用保存接口
		const saveParams: UploadToOssParams = {
			fileKey: key,
			fileSize: file.size,
			filename: file.name,
			ossFileUrl: ossFileUrl,
			fileStorePath: key,
		}

		const savedFile = await saveOssRecord(saveParams)
		return savedFile

	} catch (error) {
		console.error('OSS Upload Failed:', error)
		throw error
	}
}
